/**
 * Form validation utilities for Vue 3 applications.
 * Provides reactive form validation with support for custom validation rules,
 * backend validation errors, and native HTML5 form validation.
 * @module form_validation
 */

import { ref, reactive, computed, watch } from 'vue';

import ApiValidationError from 'JIX/errors/ApiValidationError.js';

import { __ } from 'JIX/gettext.js';

/**
 * Vue composable for form validation with reactive error handling.
 * Integrates native browser validation with custom validation rules and backend errors.
 *
 * @param {Object} options - Configuration options
 * @param {Array<Function>} [options.validation] - Array of validation functions that receive (formData, customErrors)
 * @param {Function} [options.onReset] - Callback function to execute when form is reset
 * @param {Function} options.onSubmit - Async callback function to execute on form submission
 * @returns {Object} Form validation utilities
 * @returns {Ref<HTMLFormElement>} returns.formEl - Template ref for the form element
 * @returns {Object} returns.formData - Reactive object containing form field values
 * @returns {Ref<boolean>} returns.wasValidated - Whether the form has been submitted at least once
 * @returns {ComputedRef<Object>} returns.validationErrors - Computed object with all validation errors (backend + custom)
 * @returns {Function} returns.reset - Function to reset form validation state
 * @returns {Function} returns.submit - Async function to validate and submit the form
 *
 * @example
 * const { formEl, formData, wasValidated, validationErrors, reset, submit } = useFormValidation({
 *     validation: [validDateAfter('start_date', 'end_date')],
 *     onReset: () => { formData.email = ''; },
 *     onSubmit: async () => { await api.post('/submit', formData); }
 * });
 */
export function useFormValidation({ validation, onReset, onSubmit }) {
    const formEl = ref();
    const formData = reactive({});
    const wasValidated = ref(false);
    const customErrors = ref({});
    const backendErrors = ref({});
    const validationErrors = computed(() => {
        return { ...backendErrors.value, ...customErrors.value };
    });

    // Run all custom validation functions on formData changes
    for (const fn of validation || []) {
        watch(formData, () => fn(formData, customErrors), { flush: 'sync' });
    }

    // Sync validation errors to native form validation API
    watch(() => validationErrors, () => {
        for (const el of formEl.value?.elements ?? []) {
            el.setCustomValidity(validationErrors.value[el.name] || '');
        }
    }, { deep: true, flush: 'sync' });

    /**
     * Submit the form after validation.
     * Validates the form using native validation and custom rules,
     * then calls onSubmit. Catches ApiValidationError to display backend errors.
     * @async
     */
    async function submit() {
        wasValidated.value = true;
        backendErrors.value = {};
        if (formEl.value?.reportValidity()) {
            try {
                await onSubmit();
            } catch (err) {
                if (err instanceof ApiValidationError) {
                    for (const error of err.json.errors) {
                        if (error.field) {
                            backendErrors.value[error.field] ||= error.error_message === 1
                                ? 'Fejl i indtastning'
                                : (error.error_message || __('Feltet skal udfyldes'));
                        }
                    }
                } else {
                    throw err;
                }
            }
        }
    }

    /**
     * Reset the form validation state.
     * Clears validation flags and backend errors, then calls the optional onReset callback.
     */
    function reset() {
        wasValidated.value = false;
        backendErrors.value = {};
        onReset?.();
    }

    return { formEl, formData, wasValidated, validationErrors, reset, submit };
}

/**
 * Validation function factory for date range validation.
 * Creates a validation rule that ensures an end date is not before a start date.
 *
 * @param {string} startField - Name of the form field containing the start date
 * @param {string} endField - Name of the form field containing the end date
 * @returns {Function} Validation function that can be passed to useFormValidation
 *
 * @example
 * const { formEl, formData } = useFormValidation({
 *     validation: [validDateAfter('start_date', 'end_date')],
 *     onSubmit: async () => { ... }
 * });
 */
export function validDateAfter(startField, endField) {
    return (formData, errors) => {
        if (formData[startField] && formData[endField] && formData[endField] < formData[startField]) {
            errors.value[endField] = 'Slutdato må ikke være før startdato';
        } else {
            delete errors.value[endField];
        }
    };
}
